import React, { useMemo } from 'react';

import { graphql, useFragment } from 'react-relay';

import LinkIcon from '@atlaskit/icon/core/link';
import { getProductAndIDFromARI } from '@atlassian/search-client/ari';

import type { SearchResultProps } from '../../../../../../common/types';
import { DescriptionBody } from '../../../../../../common/ui/description-body';
import { SearchResult } from '../../../../../../common/ui/search-result';
import type { AdvancedSearchResultInteractedMetadata } from '../../../../../../common/utils/events/advanced-search-result-interacted';
import { enrichExcerpt, processExcerpt } from '../../../../../../common/utils/search-result-utils';
import { getSiteMetadata } from '../../../../../../common/utils/sites';
import { ResultAnalytics } from '../../../../../../controllers/analytics/result-analytics';
import type { ResultDerivedDataProps } from '../../../result-derived-data';
import type { searchDefaultRenderer_AtlassianSearchPage$key } from './__generated__/searchDefaultRenderer_AtlassianSearchPage.graphql';

export const SearchDefaultRenderer = ({
	preparedResultAttributes,
	intentDetectionTriggered,
	node,
	position,
	cloudId,
	selectedProduct,
	primaryProduct,
	primarySubProduct,
	appMode,
	mainContentWidth,
	selectedSites,
	productConfigs,
}: SearchResultProps<searchDefaultRenderer_AtlassianSearchPage$key> &
	ResultDerivedDataProps): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchDefaultRenderer_AtlassianSearchPage on SearchResult {
				__typename
				id
				title
				description
				type
				url
			}
		`,
		node,
	);
	const { id, url, type, title, description } = data;

	const [productFromAri, , , contentId] = useMemo(() => getProductAndIDFromARI(id), [id]);

	const metadata = useMemo(() => {
		return {
			id,
			containerId: null,
			spaceId: null,
			contentType: type,
			entityType: type,
			contentId,
			product: productFromAri,
			...getSiteMetadata(id, cloudId, selectedSites),
		} satisfies AdvancedSearchResultInteractedMetadata;
	}, [id, type, contentId, productFromAri, cloudId, selectedSites]);

	const enrichedTitle = enrichExcerpt(title);
	const encrichedBody = description && (
		<DescriptionBody description={processExcerpt(description)} />
	);

	return (
		<ResultAnalytics
			preparedResultAttributes={preparedResultAttributes}
			intentDetectionTriggered={intentDetectionTriggered}
			metadata={metadata}
			position={position}
		>
			{({
				onResultClicked,
				onResultContextMenu: onResultCtxMenuClicked,
				onCopyLinkButtonClicked,
				onSummarizeInChatButtonClicked,
				onContextMenuCopyButtonClicked,
				onContextMenuButtonClicked,
			}) => (
				<SearchResult
					productConfigs={productConfigs}
					{...data}
					product={productFromAri}
					productSelected={!!selectedProduct}
					primaryProduct={primaryProduct}
					primarySubProduct={primarySubProduct}
					onResultClicked={onResultClicked}
					onResultContextMenu={onResultCtxMenuClicked}
					onCopyLinkButtonClicked={onCopyLinkButtonClicked}
					onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
					onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
					onContextMenuButtonClicked={onContextMenuButtonClicked}
					url={url}
					title={enrichedTitle}
					icon={<Fallback1PIcon />}
					headerMetadataParts={[]}
					description={encrichedBody}
					cloudId={cloudId}
					position={position}
					partial={data}
					appMode={appMode}
					mainContentWidth={mainContentWidth}
					selectedSites={selectedSites}
				/>
			)}
		</ResultAnalytics>
	);
};

export const Fallback1PIcon = (): React.JSX.Element => (
	<LinkIcon color="currentColor" spacing="spacious" label="" />
);
